import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { encodeData } from "../../helper/common/encodeData";
import {
  BACKEND_SEARCH_DEBOUNCE_MS,
  BACKEND_SEARCH_LIMIT,
} from "../../components/Filter/subComponents/filterHelper";

function getClientId(clientId) {
  if (clientId) return clientId;
  try {
    return JSON.parse(localStorage.getItem("clientId"));
  } catch {
    return null;
  }
}

async function fetchPortfolioOptions(clientId, searchTerm) {
  const term = String(searchTerm ?? "").trim();
  const params = [
    "source=amazon",
    "fields=name,platform_id",
    `limit=${BACKEND_SEARCH_LIMIT}`,
  ];
  if (term) {
    params.push(
      `filtering=${encodeData([
        { field_name: "name", operator: "CONTAINS", value: term },
      ])}`
    );
  }

  const response = await axios.get(
    `${process.env.REACT_APP_ADS_API}/client/${clientId}/portfolios?${params.join("&")}`
  );

  return (response?.data?.data || [])
    .filter((item) => item?.name)
    .map((item) => ({
      name: item.name,
      platform_id: item.platform_id,
    }));
}

/**
 * Multi portfolio picker. Options come from the same portfolios API
 * Targeting V2 uses for Portfolio Name (CONTAINS search, limited page).
 */
export default function DashboardPortfolioSelect({
  clientId,
  selectedNames = [],
  onApply,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftNames, setDraftNames] = useState(selectedNames);
  const idByNameRef = useRef({});
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const applyingRef = useRef(false);

  useEffect(() => {
    setDraftNames(selectedNames);
  }, [selectedNames]);

  const rememberOptions = useCallback((items) => {
    items.forEach((item) => {
      if (item?.name && item.platform_id) {
        idByNameRef.current[item.name] = item.platform_id;
      }
    });
  }, []);

  const loadOptions = useCallback(
    async (searchTerm) => {
      const resolvedClientId = getClientId(clientId);
      if (!resolvedClientId) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);
      try {
        const data = await fetchPortfolioOptions(resolvedClientId, searchTerm);
        if (requestIdRef.current !== requestId) return;
        rememberOptions(data);
        setOptions(data);
      } catch (error) {
        if (requestIdRef.current === requestId) {
          console.error("Error fetching portfolios:", error);
          setOptions([]);
        }
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [clientId, rememberOptions]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const scheduleLoad = useCallback(
    (searchTerm) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        loadOptions(searchTerm);
      }, BACKEND_SEARCH_DEBOUNCE_MS);
    },
    [loadOptions]
  );

  const autocompleteOptions = useMemo(() => {
    const byName = new Map(options.map((option) => [option.name, option]));
    (draftNames || []).forEach((name) => {
      if (!byName.has(name)) {
        byName.set(name, {
          name,
          platform_id: idByNameRef.current[name],
        });
      }
    });
    return Array.from(byName.values());
  }, [options, draftNames]);

  const selectedValue = useMemo(
    () =>
      (draftNames || []).map(
        (name) =>
          autocompleteOptions.find((option) => option.name === name) || {
            name,
          }
      ),
    [draftNames, autocompleteOptions]
  );

  const commit = (names) => {
    const next = names || [];
    const same =
      next.length === selectedNames.length &&
      next.every((name, index) => name === selectedNames[index]);
    if (same) return;
    const idsByName = {};
    next.forEach((name) => {
      if (idByNameRef.current[name]) {
        idsByName[name] = idByNameRef.current[name];
      }
    });
    onApply?.(next, idsByName);
  };

  const closeWithoutApply = () => {
    applyingRef.current = false;
    setDraftNames(selectedNames);
    setInputValue("");
    setOpen(false);
  };

  const applySelection = () => {
    applyingRef.current = true;
    commit(draftNames);
    setInputValue("");
    setOpen(false);
  };

  const visibleNames = autocompleteOptions.map((option) => option.name);
  const selectedVisibleCount = visibleNames.filter((name) =>
    draftNames.includes(name)
  ).length;
  const allVisibleSelected =
    visibleNames.length > 0 && selectedVisibleCount === visibleNames.length;

  const toggleSelectAll = () => {
    if (!visibleNames.length) return;
    if (allVisibleSelected) {
      const visible = new Set(visibleNames);
      setDraftNames(draftNames.filter((name) => !visible.has(name)));
      return;
    }
    rememberOptions(autocompleteOptions);
    setDraftNames(Array.from(new Set([...draftNames, ...visibleNames])));
  };

  return (
    <Autocomplete
      multiple
      size="small"
      open={open}
      options={autocompleteOptions}
      value={selectedValue}
      inputValue={inputValue}
      loading={loading}
      disableCloseOnSelect
      disableClearable
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(option, value) => option?.name === value?.name}
      filterOptions={(current) => current}
      noOptionsText={loading ? "Searching portfolios..." : "No portfolios found"}
      onOpen={() => {
        setDraftNames(selectedNames);
        setOpen(true);
        loadOptions("");
      }}
      onClose={() => {
        if (applyingRef.current) {
          applyingRef.current = false;
          setInputValue("");
          setOpen(false);
          return;
        }
        closeWithoutApply();
      }}
      onInputChange={(_, value, reason) => {
        if (reason === "reset") return;
        setInputValue(value);
        if (reason === "input") scheduleLoad(value);
      }}
      onChange={(_, value) => {
        rememberOptions(value || []);
        setDraftNames((value || []).map((option) => option.name));
      }}
      renderTags={(value) => {
        if (open || !value.length) return null;
        const extra = value.length - 1;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              maxWidth: "calc(100% - 28px)",
              overflow: "hidden",
            }}
          >
            <Typography noWrap sx={{ fontSize: "14px" }}>
              {value[0].name}
            </Typography>
            {extra > 0 ? (
              <Typography
                sx={{ ml: 0.5, flexShrink: 0, fontSize: "14px", color: "text.secondary" }}
              >
                +{extra}
              </Typography>
            ) : null}
          </Box>
        );
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
          {option.name}
        </li>
      )}
      PaperComponent={(paperProps) => {
        const { children, ...rest } = paperProps;
        return (
          <Paper {...rest}>
            <Box
              onMouseDown={(event) => event.preventDefault()}
              onClick={toggleSelectAll}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 0.75,
                cursor: visibleNames.length ? "pointer" : "default",
                borderBottom: "1px solid #E0E0E0",
                opacity: visibleNames.length ? 1 : 0.5,
              }}
            >
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                disabled={!visibleNames.length}
                sx={{ mr: 1, p: 0.5, pointerEvents: "none" }}
              />
              <Typography sx={{ fontSize: "14px" }}>Select All</Typography>
            </Box>
            {children}
            <Box
              onMouseDown={(event) => event.preventDefault()}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                px: 1.5,
                py: 1,
                borderTop: "1px solid #E0E0E0",
              }}
            >
              <Button size="small" variant="outlined" onClick={closeWithoutApply}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={applySelection}>
                Apply
              </Button>
            </Box>
          </Paper>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Choose Portfolios"
          placeholder={open || !selectedValue.length ? "Enter portfolio name" : ""}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        width: 320,
        bgcolor: "#fff",
        "& .MuiAutocomplete-inputRoot": {
          flexWrap: "nowrap",
        },
        "& .MuiAutocomplete-input": {
          minWidth: open ? "80px !important" : "0 !important",
        },
      }}
    />
  );
}
